import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Inbox, FileText, Quote, LogOut, Trash2, Pencil, Plus, Loader2,
  Mail, Phone, DollarSign, HandHelping, Package, BookOpen, ExternalLink,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  clearToken, getSummary, getSubmissions, deleteSubmission,
  adminGetBlog, createBlog, updateBlog, deleteBlog,
  adminGetTestimonials, createTestimonial, updateTestimonial, deleteTestimonial,
} from "@/lib/adminApi";

const KIND_META = {
  donation: { label: "Donations", icon: DollarSign },
  volunteer: { label: "Volunteers", icon: HandHelping },
  goods: { label: "Goods", icon: Package },
  contact: { label: "Messages", icon: Mail },
  prayer: { label: "Prayer", icon: BookOpen },
};

const fmt = (d) => (d ? new Date(d).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "");

const NAV = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "submissions", label: "Submissions", icon: Inbox },
  { id: "blog", label: "Blog Posts", icon: FileText },
  { id: "testimonials", label: "Testimonials", icon: Quote },
];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");

  const logout = () => { clearToken(); nav("/admin/login"); };

  return (
    <div className="min-h-screen bg-cream flex">
      {/* Sidebar */}
      <aside className="w-64 bg-plum-dark text-cream flex-col hidden md:flex fixed inset-y-0 left-0" data-testid="admin-sidebar">
        <div className="p-6 border-b border-cream/10 flex items-center gap-3">
          <span className="grid place-items-center w-10 h-10 rounded-full bg-gold text-plum font-heading text-xl font-semibold">G</span>
          <div>
            <p className="font-heading text-lg leading-tight">Greater Faith</p>
            <p className="text-cream/50 text-xs">Admin Panel</p>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => setTab(n.id)} data-testid={`admin-nav-${n.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${tab === n.id ? "bg-gold text-plum" : "text-cream/80 hover:bg-cream/10"}`}>
              <n.icon size={18} /> {n.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-cream/10 space-y-1">
          <a href="/" target="_blank" rel="noreferrer" className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-cream/70 hover:bg-cream/10"><ExternalLink size={18} /> View Website</a>
          <button onClick={logout} data-testid="admin-logout" className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-cream/70 hover:bg-cream/10"><LogOut size={18} /> Sign Out</button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 md:ml-64 min-w-0">
        {/* Mobile top nav */}
        <div className="md:hidden bg-plum-dark text-cream p-4 flex gap-2 overflow-x-auto no-scrollbar sticky top-0 z-20">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => setTab(n.id)} className={`whitespace-nowrap px-4 py-2 rounded-full text-sm ${tab === n.id ? "bg-gold text-plum" : "bg-cream/10"}`}>{n.label}</button>
          ))}
          <button onClick={logout} className="whitespace-nowrap px-4 py-2 rounded-full text-sm bg-cream/10">Sign Out</button>
        </div>

        <div className="p-6 sm:p-10 max-w-6xl">
          {tab === "overview" && <Overview go={setTab} />}
          {tab === "submissions" && <Submissions />}
          {tab === "blog" && <BlogManager />}
          {tab === "testimonials" && <TestimonialManager />}
        </div>
      </main>
    </div>
  );
}

function Overview({ go }) {
  const [s, setS] = useState(null);
  useEffect(() => { getSummary().then(setS).catch(() => toast.error("Failed to load summary")); }, []);
  return (
    <div data-testid="admin-overview">
      <h1 className="font-heading text-4xl text-plum font-semibold">Welcome back</h1>
      <p className="text-stone mt-2">Here's what's happening across the ministry site.</p>
      {!s ? <div className="mt-10"><Loader2 className="animate-spin text-plum" /></div> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
            {Object.entries(KIND_META).map(([k, m]) => (
              <button key={k} onClick={() => go("submissions")} className="text-left bg-white rounded-2xl border border-plum/10 p-6 lift">
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-plum/5 text-plum mb-3"><m.icon size={20} /></span>
                <p className="font-heading text-4xl text-plum font-semibold">{s[k] ?? 0}</p>
                <p className="text-stone text-sm">{m.label}</p>
              </button>
            ))}
            <div className="bg-plum rounded-2xl p-6 text-cream">
              <p className="font-heading text-4xl font-semibold">{s.total ?? 0}</p>
              <p className="text-cream/70 text-sm">Total Submissions</p>
              <div className="mt-3 pt-3 border-t border-cream/15 text-sm text-cream/70">
                {s.posts} blog posts &bull; {s.testimonials} testimonials
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Submissions() {
  const [kind, setKind] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    getSubmissions(kind === "all" ? undefined : kind).then(setItems).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  };
  useEffect(load, [kind]);

  const remove = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try { await deleteSubmission(id); setItems((x) => x.filter((i) => i.id !== id)); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-submissions">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl text-plum font-semibold">Submissions</h1>
          <p className="text-stone mt-2">Donations, volunteers, goods, messages, and prayer sign-ups.</p>
        </div>
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="w-52 bg-white border-plum/15" data-testid="submissions-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {Object.entries(KIND_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? <div className="mt-10"><Loader2 className="animate-spin text-plum" /></div> : items.length === 0 ? (
        <p className="text-stone mt-10">No submissions yet.</p>
      ) : (
        <div className="mt-8 space-y-4">
          {items.map((it) => (
            <div key={it.id} className="bg-white rounded-2xl border border-plum/10 p-5" data-testid="submission-card">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs uppercase tracking-wide font-semibold text-plum bg-plum/5 rounded-full px-3 py-1">{KIND_META[it.kind]?.label || it.kind}</span>
                    <span className="text-stone text-xs">{fmt(it.created_at)}</span>
                  </div>
                  <p className="font-semibold text-plum mt-2">{it.name}</p>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-stone mt-1">
                    {it.email && <a href={`mailto:${it.email}`} className="flex items-center gap-1.5 hover:text-plum"><Mail size={13} />{it.email}</a>}
                    {it.phone && <a href={`tel:${it.phone}`} className="flex items-center gap-1.5 hover:text-plum"><Phone size={13} />{it.phone}</a>}
                  </div>
                  {it.message && <p className="text-ink/80 text-sm mt-3 leading-relaxed">{it.message}</p>}
                  {it.meta && Object.keys(it.meta).length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {Object.entries(it.meta).map(([k, v]) => (
                        <span key={k} className="text-xs bg-cream border border-plum/10 rounded-lg px-2.5 py-1 text-stone">
                          <span className="text-plum font-medium">{k}:</span> {Array.isArray(v) ? v.join(", ") : String(v)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <button onClick={() => remove(it.id)} data-testid="submission-delete" className="text-stone hover:text-destructive shrink-0 p-2"><Trash2 size={18} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const EMPTY_POST = { slug: "", title: "", excerpt: "", body: "", category: "", author: "Greater Faith Team", image: "", date: new Date().toISOString().slice(0, 10), read_minutes: 4 };

function BlogManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_POST);
  const [saving, setSaving] = useState(false);

  const load = () => { setLoading(true); adminGetBlog().then(setItems).catch(() => toast.error("Failed to load")).finally(() => setLoading(false)); };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm(EMPTY_POST); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY_POST, ...p }); setOpen(true); };

  const save = async () => {
    if (!form.title || !form.slug || !form.excerpt || !form.body || !form.category || !form.image) { toast.error("Please fill all fields (title, slug, excerpt, body, category, image)."); return; }
    setSaving(true);
    try {
      if (editing) { await updateBlog(editing.id, { ...form, read_minutes: Number(form.read_minutes) || 4 }); toast.success("Post updated"); }
      else { await createBlog({ ...form, read_minutes: Number(form.read_minutes) || 4 }); toast.success("Post created"); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try { await deleteBlog(p.id); setItems((x) => x.filter((i) => i.id !== p.id)); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-blog">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl text-plum font-semibold">Blog Posts</h1>
          <p className="text-stone mt-2">Write and manage stories for the site.</p>
        </div>
        <button onClick={openNew} data-testid="blog-new-btn" className="bg-plum hover:bg-plum-dark text-cream font-semibold rounded-full px-5 py-3 flex items-center gap-2"><Plus size={18} /> New Post</button>
      </div>

      {loading ? <div className="mt-10"><Loader2 className="animate-spin text-plum" /></div> : (
        <div className="mt-8 space-y-3">
          {items.map((p) => (
            <div key={p.id} className="bg-white rounded-2xl border border-plum/10 p-4 flex items-center gap-4" data-testid="blog-row">
              <img src={p.image} alt="" className="w-16 h-16 rounded-xl object-cover shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-plum truncate">{p.title}</p>
                <p className="text-stone text-sm">{p.category} &bull; {p.date}</p>
              </div>
              <button onClick={() => openEdit(p)} className="text-stone hover:text-plum p-2" data-testid="blog-edit"><Pencil size={17} /></button>
              <button onClick={() => remove(p)} className="text-stone hover:text-destructive p-2" data-testid="blog-delete"><Trash2 size={17} /></button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-cream max-h-[90vh] overflow-y-auto" data-testid="blog-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-plum">{editing ? "Edit Post" : "New Post"}</DialogTitle><DialogDescription>Fill in the story details below. The body supports blank lines between paragraphs.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-plum text-sm">Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="blog-title" className="bg-white border-plum/15 mt-1" /></div>
              <div><Label className="text-plum text-sm">Slug (url)</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") })} data-testid="blog-slug" className="bg-white border-plum/15 mt-1" /></div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div><Label className="text-plum text-sm">Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="blog-category" className="bg-white border-plum/15 mt-1" /></div>
              <div><Label className="text-plum text-sm">Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-white border-plum/15 mt-1" /></div>
              <div><Label className="text-plum text-sm">Read (min)</Label><Input type="number" value={form.read_minutes} onChange={(e) => setForm({ ...form, read_minutes: e.target.value })} className="bg-white border-plum/15 mt-1" /></div>
            </div>
            <div><Label className="text-plum text-sm">Image URL</Label><Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} data-testid="blog-image" className="bg-white border-plum/15 mt-1" placeholder="https://..." /></div>
            <div><Label className="text-plum text-sm">Excerpt</Label><Textarea value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} data-testid="blog-excerpt" rows={2} className="bg-white border-plum/15 mt-1" /></div>
            <div><Label className="text-plum text-sm">Body (use blank lines for paragraphs)</Label><Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="blog-body" rows={8} className="bg-white border-plum/15 mt-1" /></div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-5 py-2.5 rounded-full text-plum border border-plum/20">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="blog-save" className="px-6 py-2.5 rounded-full bg-plum text-cream font-semibold flex items-center gap-2 disabled:opacity-60">{saving && <Loader2 className="animate-spin" size={16} />}Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const EMPTY_T = { name: "", role: "", quote: "", program: "", image: "" };

function TestimonialManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_T);
  const [saving, setSaving] = useState(false);

  const load = () => { setLoading(true); adminGetTestimonials().then(setItems).catch(() => toast.error("Failed to load")).finally(() => setLoading(false)); };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm(EMPTY_T); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...EMPTY_T, ...t }); setOpen(true); };

  const save = async () => {
    if (!form.quote || !form.role || !form.program) { toast.error("Quote, role and program are required."); return; }
    setSaving(true);
    try {
      if (editing) { await updateTestimonial(editing.id, form); toast.success("Updated"); }
      else { await createTestimonial(form); toast.success("Created"); }
      setOpen(false); load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try { await deleteTestimonial(t.id); setItems((x) => x.filter((i) => i.id !== t.id)); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-testimonials">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl text-plum font-semibold">Testimonials</h1>
          <p className="text-stone mt-2">Manage the stories of God's faithfulness.</p>
        </div>
        <button onClick={openNew} data-testid="testimonial-new-btn" className="bg-plum hover:bg-plum-dark text-cream font-semibold rounded-full px-5 py-3 flex items-center gap-2"><Plus size={18} /> New</button>
      </div>

      {loading ? <div className="mt-10"><Loader2 className="animate-spin text-plum" /></div> : (
        <div className="mt-8 space-y-3">
          {items.map((t) => (
            <div key={t.id} className="bg-white rounded-2xl border border-plum/10 p-4 flex items-center gap-4" data-testid="testimonial-row">
              {t.image ? <img src={t.image} alt="" className="w-14 h-14 rounded-full object-cover shrink-0" /> : <span className="grid place-items-center w-14 h-14 rounded-full bg-plum/5 text-plum shrink-0"><Quote size={18} /></span>}
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-plum">{t.name || "(no name)"}</p>
                <p className="text-stone text-sm truncate">{t.role} &bull; {t.program}</p>
              </div>
              <button onClick={() => openEdit(t)} className="text-stone hover:text-plum p-2" data-testid="testimonial-edit"><Pencil size={17} /></button>
              <button onClick={() => remove(t)} className="text-stone hover:text-destructive p-2" data-testid="testimonial-delete"><Trash2 size={17} /></button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-cream max-h-[90vh] overflow-y-auto" data-testid="testimonial-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-plum">{editing ? "Edit Testimonial" : "New Testimonial"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-plum text-sm">Name (optional)</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="t-name" className="bg-white border-plum/15 mt-1" /></div>
              <div><Label className="text-plum text-sm">Role</Label><Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="t-role" className="bg-white border-plum/15 mt-1" placeholder="e.g. Blessed With a Vehicle" /></div>
            </div>
            <div><Label className="text-plum text-sm">Program</Label><Input value={form.program} onChange={(e) => setForm({ ...form, program: e.target.value })} data-testid="t-program" className="bg-white border-plum/15 mt-1" placeholder="e.g. Naomi's Guidance" /></div>
            <div><Label className="text-plum text-sm">Photo URL (optional)</Label><Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} data-testid="t-image" className="bg-white border-plum/15 mt-1" placeholder="https://..." /></div>
            <div><Label className="text-plum text-sm">Quote</Label><Textarea value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} data-testid="t-quote" rows={6} className="bg-white border-plum/15 mt-1" /></div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-5 py-2.5 rounded-full text-plum border border-plum/20">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="t-save" className="px-6 py-2.5 rounded-full bg-plum text-cream font-semibold flex items-center gap-2 disabled:opacity-60">{saving && <Loader2 className="animate-spin" size={16} />}Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
